import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import { Radio, Calendar, Clock, Users, Play } from 'lucide-react';
import PageMeta from '../components/PageMeta';

const LiveStream = () => {
  const { language } = useLanguage();
  const [livestream, setLivestream] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLivestream();
    
    // Check for live streams every 2 minutes
    const interval = setInterval(() => {
      fetchLivestream();
    }, 120000); // 2 minutes
    
    return () => clearInterval(interval);
  }, []);

  const fetchLivestream = async () => {
    try {
      const response = await api.get('/livestream/active');
      const data = response.data;
      
      if (data && data.video_id) {
        // YouTube live stream found
        setLivestream({
          is_live: data.is_live,
          video_id: data.video_id,
          title_en: data.title,
          title_ti: data.title, // TODO: Could add translation if available
          stream_url: `https://www.youtube.com/embed/${data.video_id}?autoplay=1`,
          scheduled_time: data.started_at,
          thumbnail: data.thumbnail
        });
      } else if (data) {
        // Database livestream (fallback)
        setLivestream(data);
      } else {
        // No live stream
        setLivestream(null);
      }
    } catch (error) {
      console.error('Error fetching livestream:', error);
      setLivestream(null);
    } finally {
      setLoading(false);
    }
  };

  const upcomingServices = [
    {
      id: 1,
      title_en: 'Divine Liturgy',
      title_ti: 'ቅዳሴ',
      time_en: 'Monthly on Sunday at 7:00 AM',
      time_ti: 'ወርሓዊ ሰንበት ሰዓት 7:00 ንጉሆ',
      description_en: 'Join us for our weekly Divine Liturgy service',
      description_ti: 'ንሰሙናዊ ጸሎትን ቃል-ኣምላክን ምሳና ተሳተፉ'
    },
    {
      id: 2,
      title_en: 'Sunday School',
      title_ti: 'ትምህርቲ ሰንበት',
      time_en: 'Every Sunday at 10:30 AM',
      time_ti: 'ኩሉ ሰንበት ሰዓት 10:30 ንጉሆ',
      description_en: 'Youth and children gathering with teachings',
      description_ti: 'መድብ ትምህርቲ ን መንእሰያትን ህጻናትን'
    },
    {
      id: 3,
      title_en: "Women's Ministry",
      title_ti: 'ናይ ደቂ ኣንስትዮ ኣገልግሎት',
      time_en: 'Every Sunday at 11:30 AM',
      time_ti: 'ኩሉ ሰንበት ሰዓት 11:30 ንጉሆ',
      description_en: 'Women gathering with fellowship and prayer',
      description_ti: 'ትምህርቲ ሰንበት ደቂ ኣንስትዮ'
    }
  ];

  return (


    <>


      <PageMeta 


        title="Live Stream"


        path="/livestream"


        description="Watch our live church services online. Join us for worship, prayer, and sermons from anywhere."


      />
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-red-100 via-amber-50 to-orange-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Radio className="w-16 h-16 text-red-600 dark:text-red-500 mx-auto mb-6 animate-pulse" />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
            {t(language, 'livestream.title')}
          </h1>
          <p className="text-lg text-gray-700 dark:text-gray-300">
            {t(language, 'livestream.subtitle')}
          </p>
        </div>
      </section>

      {/* Live Stream Player */}
      <section className="py-16 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {livestream && livestream.is_live ? (
            <div>
              <div className="mb-6 flex items-center justify-center space-x-3">
                <div className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-full">
                  <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                  <span className="font-bold">{t(language, 'livestream.liveNow')}</span>
                </div>
                <div className="flex items-center space-x-2 text-white">
                  <Users className="w-5 h-5" />
                  <span>{t(language, 'livestream.watchingNow')}</span>
                </div>
              </div>
              
              <div className="aspect-video rounded-lg overflow-hidden shadow-2xl">
                <iframe
                  width="100%"
                  height="100%"
                  src={livestream.stream_url}
                  title={language === 'en' ? livestream.title_en : livestream.title_ti}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="livestream-player"
                ></iframe>
              </div>
              
              <div className="mt-6 text-center">
                <h2 className="text-2xl font-bold text-white mb-2">
                  {language === 'en' ? livestream.title_en : livestream.title_ti}
                </h2>
                <p className="text-gray-400">
                  {t(language, 'livestream.startedAt')} {new Date(livestream.scheduled_time).toLocaleTimeString()}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-20">
              <Radio className="w-20 h-20 text-gray-600 dark:text-gray-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">
                {t(language, 'livestream.noLiveStream')}
              </h2>
              <p className="text-gray-400 mb-8">
                {t(language, 'livestream.noLiveMessage')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                {/* Watch Latest Sermons Button */}
                <Link
                  to="/sermons"
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-full transition-colors"
                  data-testid="watch-sermons-link"
                >
                  <Play className="w-5 h-5" />
                  <span>{t(language, 'livestream.watchLatestSermons')}</span>
                </Link>
                
                {/* YouTube Channel Link */}
                <a
                  href="https://www.youtube.com/@MekaneHiwetEyesusDallas"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors"
                  data-testid="youtube-channel-link"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  <span>{t(language, 'livestream.visitYouTube')}</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Service Schedule */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Calendar className="w-12 h-12 text-amber-600 dark:text-amber-500 mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
              {t(language, 'livestream.serviceSchedule')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t(language, 'livestream.serviceScheduleSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {upcomingServices.map((service) => (
              <div
                key={service.id}
                className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center justify-center w-12 h-12 bg-amber-600 rounded-full mb-4">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-amber-800 dark:text-amber-400 mb-2">
                  {language === 'en' ? service.title_en : service.title_ti}
                </h3>
                <p className="text-gray-700 dark:text-gray-300 font-semibold mb-3">
                  {language === 'en' ? service.time_en : service.time_ti}
                </p>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {language === 'en' ? service.description_en : service.description_ti}
                </p>
              </div>
            ))}
          </div>

          {/* Location Info */}
          <div className="mt-12 bg-amber-50 dark:bg-gray-800 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-amber-800 dark:text-amber-400 mb-4">
              {t(language, 'livestream.joinInPerson')}
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              8822 Angora St, Dallas, TX 75218
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t(language, 'livestream.currentMeetingLocation')}
            </p>
          </div>
        </div>
      </section>

      {/* How to Watch */}
      <section className="py-16 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12" style={{fontFamily: 'Playfair Display, serif'}}>
            {t(language, 'livestream.howToWatch')}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">
                {language === 'en' ? 'Visit This Page' : 'ቀጥታ መድብ'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {language === 'en' 
                  ? 'Come to our Live Stream page during service times'
                  : 'ኣብ ግዜ ኣገልግሎት ኣብ ናይ ቀጥታ መድብ ገጽ ተመልከቱ'
                }
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">
                {language === 'en' ? 'Or YouTube' : 'ይቱብ (YouTube)'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {language === 'en' 
                  ? 'Subscribe to our YouTube channel for notifications'
                  : 'ንቀጻሊ ፈነወ ኣብ YouTube ቻናልና ተመዝጊብኩም ተካታተሉ'
                }
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">
                {language === 'en' ? 'Watch & Worship' : ' ኣምልኹን ተመልከቱን'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {language === 'en' 
                  ? 'Join from anywhere in the world'
                  : 'ካብ ዝኾነ ክፍለ ዓለም ሕበሩና'
                }
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default LiveStream;